/**
 * ExpressionAssessment Component
 * Adds Azure pronunciation assessment with Bottom Sheet UI
 */

import { useState, useRef } from "react";
import { ExpressionAudioPlayer } from "@/components/ExpressionAudioPlayer";
import { Button } from "@/components/ui/button";
import {
  XCircle,
  Loader2,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAzureSpeech } from "@/hooks/useAzureSpeech";
import type { PreparedExpression } from "@/types/meeting-preparation.types";
import type {
  PronunciationResult,
  WordAssessment,
} from "@/types/pronunciation.types";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { logger } from "@/utils/logger";

interface ExpressionAssessmentProps {
  expression: PreparedExpression;
  modelAudioUrl?: string;
  expressionNumber: number;
  totalExpressions: number;
  className?: string;
  onNextExpression?: () => void; // Callback para "Próxima Expressão"
  onComplete?: () => void; // Callback quando terminar (última expressão)
  isLastExpression?: boolean; // Flag se é a última expressão
}

export const ExpressionAssessment = ({
  expression,
  modelAudioUrl,
  expressionNumber,
  totalExpressions,
  className,
  onNextExpression,
  onComplete,
  isLastExpression,
}: ExpressionAssessmentProps) => {
  const [feedback, setFeedback] = useState<{
    score: number;
    message: string;
    show: boolean;
    result?: PronunciationResult; // Guardar resultado completo para análise detalhada
    recordingUrl?: string; // URL do blob de áudio do usuário
  } | null>(null);

  const [showTroubleshooting, setShowTroubleshooting] = useState(false);

  const { assessPronunciation, isProcessing } = useAzureSpeech();
  const previousStreamRef = useRef<SpeechSDK.PushAudioInputStream | null>(null);

  // Function to detect audio quality issues based on word scores
  const detectAudioQualityIssues = (
    words: WordAssessment[]
  ): "critical" | "low" | "normal" => {
    if (!words || words.length === 0) return "normal";

    const totalWords = words.length;
    const lowScoreWords = words.filter((word) => {
      const score = Math.round(
        word.PronunciationAssessment?.AccuracyScore || 0
      );
      return score <= 10;
    }).length;

    const zeroScoreWords = words.filter((word) => {
      const score = Math.round(
        word.PronunciationAssessment?.AccuracyScore || 0
      );
      return score === 0;
    }).length;

    // Critical: All words have score = 0
    if (zeroScoreWords === totalWords) {
      return "critical";
    }

    // Low quality: >=70% of words have score <= 10
    const lowScorePercentage = (lowScoreWords / totalWords) * 100;
    if (lowScorePercentage >= 70) {
      return "low";
    }

    return "normal";
  };

  // Handler para quando nova gravação INICIA (limpa feedback anterior)
  const handleRecordingStart = () => {
    logger.log("[ExpressionAssessment] New recording started, hiding feedback");
    setFeedback(null);
  };

  // Handler para quando gravação termina - OPTIMIZED: recebe PushStream direto
  const handleRecordingComplete = async (
    pushStream: SpeechSDK.PushAudioInputStream,
    recordingBlob?: Blob
  ) => {
    // Evita reprocessar o mesmo stream
    if (pushStream === previousStreamRef.current) {
      logger.log("[ExpressionAssessment] Same stream, skipping");
      return;
    }

    previousStreamRef.current = pushStream;

    try {
      logger.log("[ExpressionAssessment] ===== SENDING TO AZURE =====");
      logger.log(
        "[ExpressionAssessment] Using direct PushStream (ZERO latency)"
      );
      logger.log("[ExpressionAssessment] Expression:", expression.expression);

      const result = await assessPronunciation(
        pushStream,
        expression.expression
      );

      logger.log("[ExpressionAssessment] ===== AZURE RESULT =====", result);

      if (result?.NBest?.[0]?.PronunciationAssessment) {
        const score = Math.round(
          result.NBest[0].PronunciationAssessment.PronScore
        );
        logger.log("[ExpressionAssessment] Score:", score);

        // Se temos Blob para reprodução, criar URL
        const recordingUrl = recordingBlob
          ? URL.createObjectURL(recordingBlob)
          : undefined;

        setFeedback({
          score,
          message:
            score >= 80
              ? "Great job!"
              : score >= 60
                ? "Good! Keep practicing."
                : "Keep trying!",
          show: true,
          result, // Guardar resultado completo
          recordingUrl, // URL do áudio do usuário
        });

        // Reset troubleshooting state for new assessment
        setShowTroubleshooting(false);

        // Feedback fica visível até nova gravação (sem auto-hide)
      }
    } catch (error) {
      logger.error("[ExpressionAssessment] ===== AZURE ERROR =====", error);
      // Mostra feedback de erro
      setFeedback({
        score: 0,
        message: "Error analyzing pronunciation. Please try again.",
        show: true,
      });
    }
  };

  const getScoreColorBg = (score: number) => {
    if (score >= 80) return "bg-emerald-50 border-emerald-200";
    if (score >= 60) return "bg-amber-50 border-amber-200";
    return "bg-rose-50 border-rose-200";
  };

  const getScoreColorText = (score: number) => {
    if (score >= 80) return "text-emerald-600";
    if (score >= 60) return "text-amber-600";
    return "text-rose-600";
  };

  const getWordScoreColor = (score: number) => {
    if (score >= 80)
      return "bg-emerald-100 text-emerald-700 border-emerald-300";
    if (score >= 60) return "bg-amber-100 text-amber-700 border-amber-300";
    return "bg-rose-100 text-rose-700 border-rose-300";
  };

  return (
    <>
      {/* Player original */}
      <div className={className}>
        <ExpressionAudioPlayer
          expression={expression}
          audioBlobUrl={modelAudioUrl}
          expressionNumber={expressionNumber}
          totalExpressions={totalExpressions}
          onRecordingComplete={handleRecordingComplete}
          onRecordingStart={handleRecordingStart}
        />
      </div>

      {/* Processing indicator - MODAL FIXO */}
      {isProcessing && !feedback?.show && (
        <>
          {/* Backdrop escuro */}
          <div className="fixed inset-0 bg-black/60 z-50" />

          {/* Modal de loading */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-8 shadow-2xl max-w-md w-full">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
                <p className="text-lg font-medium text-gray-900">
                  Analyzing pronunciation...
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* MODAL FIXO - Resultado da avaliação */}
      {feedback?.show && feedback.result && (
        <>
          {/* Backdrop escuro */}
          <div className="fixed inset-0 bg-black/80 z-50" />

          {/* Modal centralizado */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className={cn(
                "rounded-3xl border-2 overflow-hidden shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto",
                getScoreColorBg(feedback.score)
              )}
            >
              <div className="p-8 space-y-6">
                {/* Emoji + Mensagem */}
                <div className="text-center">
                  <div className="text-4xl mb-2">
                    {feedback.score >= 80
                      ? "😊"
                      : feedback.score >= 60
                        ? "🙂"
                        : "😐"}
                  </div>
                  <p className="text-xl font-bold text-gray-900">
                    {feedback.message}
                  </p>
                </div>

                {/* Score grande */}
                <div className="text-center">
                  <div
                    className={cn(
                      "text-7xl font-bold",
                      getScoreColorText(feedback.score)
                    )}
                  >
                    {feedback.score}
                    <span className="text-4xl opacity-70">/100</span>
                  </div>
                  <div className="mt-2 text-sm font-medium text-gray-600">
                    Overall Score
                  </div>
                </div>

                {/* Player de áudio DA GRAVAÇÃO DO USUÁRIO */}
                {feedback.recordingUrl && (
                  <div className="space-y-2 bg-white/50 rounded-xl p-4">
                    <p className="text-sm font-semibold text-gray-700">
                      Your response:
                    </p>
                    <audio
                      controls
                      src={feedback.recordingUrl}
                      className="w-full"
                      crossOrigin="anonymous"
                      controlsList="nodownload"
                      onError={(e) => {
                        const errorCode = e.currentTarget.error?.code;
                        const errorNames: Record<number, string> = {
                          1: "ABORTED",
                          2: "NETWORK",
                          3: "DECODE",
                          4: "SRC_NOT_SUPPORTED",
                        };
                        const errorName =
                          errorNames[errorCode || 0] || "UNKNOWN";
                        logger.error(
                          `[ExpressionAssessment] Recording audio error (${errorName}):`,
                          {
                            errorCode,
                            src: feedback.recordingUrl,
                          }
                        );
                      }}
                      onCanPlay={() => {
                        logger.log(
                          "[ExpressionAssessment] Recording audio ready to play:",
                          { src: feedback.recordingUrl }
                        );
                      }}
                    />
                  </div>
                )}

                {/* Word-by-word COM formatação do Display */}
                {feedback.result.NBest?.[0]?.Words &&
                  feedback.result.NBest[0].Words.length > 0 &&
                  feedback.result.NBest[0].Display && (
                    <div className="space-y-3 bg-white/50 rounded-xl p-4">
                      <div className="flex flex-wrap gap-2">
                        {(() => {
                          const displayText =
                            feedback.result.NBest[0].Display || "";
                          const words = feedback.result.NBest[0].Words;
                          const result: JSX.Element[] = [];
                          let displayIndex = 0;

                          words.forEach((word: WordAssessment, idx: number) => {
                            const score = Math.round(
                              word.PronunciationAssessment?.AccuracyScore || 0
                            );

                            // Encontrar a palavra no Display (case-insensitive)
                            const wordLower = (word.Word || "").toLowerCase();
                            const searchStart = displayIndex;
                            const foundIndex = displayText
                              .toLowerCase()
                              .indexOf(wordLower, searchStart);

                            if (foundIndex >= 0) {
                              // Pegar a palavra com formatação original (maiúsculas) do Display
                              const formattedWord = displayText.substring(
                                foundIndex,
                                foundIndex + wordLower.length
                              );

                              // Verificar se tem pontuação logo após
                              let punctuation = "";
                              let nextCharIndex = foundIndex + wordLower.length;

                              while (
                                nextCharIndex < displayText.length &&
                                /[.,!?;:]/.test(displayText[nextCharIndex])
                              ) {
                                punctuation += displayText[nextCharIndex];
                                nextCharIndex++;
                              }

                              // Atualizar índice para próxima busca
                              displayIndex = nextCharIndex;

                              result.push(
                                <span
                                  key={idx}
                                  className={cn(
                                    "px-4 py-2 rounded-full text-base font-medium border-2 relative inline-flex items-center",
                                    score >= 90
                                      ? "bg-emerald-500/20 text-emerald-800 border-emerald-400"
                                      : score >= 70
                                        ? "bg-amber-500/20 text-amber-800 border-amber-400"
                                        : "bg-rose-500/20 text-rose-800 border-rose-400"
                                  )}
                                >
                                  {formattedWord}
                                  {punctuation}
                                  <sup className="text-xs font-bold ml-0.5">
                                    {score}
                                  </sup>
                                </span>
                              );
                            } else {
                              // Fallback: usar palavra original sem formatação
                              result.push(
                                <span
                                  key={idx}
                                  className={cn(
                                    "px-4 py-2 rounded-full text-base font-medium border-2 relative",
                                    score >= 90
                                      ? "bg-emerald-500/20 text-emerald-800 border-emerald-400"
                                      : score >= 70
                                        ? "bg-amber-500/20 text-amber-800 border-amber-400"
                                        : "bg-rose-500/20 text-rose-800 border-rose-400"
                                  )}
                                >
                                  {word.Word}
                                  <sup className="text-xs font-bold ml-0.5">
                                    {score}
                                  </sup>
                                </span>
                              );
                            }
                          });

                          return result;
                        })()}
                      </div>
                    </div>
                  )}

                {/* Audio Quality Troubleshooting Section */}
                {(() => {
                  const audioQuality = feedback.result?.NBest?.[0]?.Words
                    ? detectAudioQualityIssues(feedback.result.NBest[0].Words)
                    : "normal";

                  if (audioQuality === "normal") return null;

                  return (
                    <div className="bg-white/50 rounded-xl p-4 space-y-3">
                      <button
                        onClick={() =>
                          setShowTroubleshooting(!showTroubleshooting)
                        }
                        className="w-full flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-amber-600" />
                          <span className="font-semibold text-gray-800">
                            {audioQuality === "critical"
                              ? "Audio Quality Issues Detected"
                              : "Audio Quality May Be Low"}
                          </span>
                        </div>
                        <ChevronDown
                          className={cn(
                            "h-5 w-5 text-gray-600 transition-transform",
                            showTroubleshooting && "rotate-180"
                          )}
                        />
                      </button>

                      {showTroubleshooting && (
                        <div className="space-y-2 text-sm text-gray-700">
                          <p className="font-medium">Quick fixes to try:</p>
                          <ul className="space-y-1 ml-4">
                            <li>
                              • Check that your microphone is connected and
                              working
                            </li>
                            <li>
                              • Increase microphone volume in your system
                              settings
                            </li>
                            <li>
                              • Reduce background noise or find a quieter
                              location
                            </li>
                            <li>
                              • Try using headphones with a built-in microphone
                            </li>
                            {audioQuality === "critical" && (
                              <li>
                                • Test your microphone in another application
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Botões adaptativos */}
                {isLastExpression ? (
                  <Button
                    onClick={() => {
                      setFeedback(null);
                      onComplete?.();
                    }}
                    className="w-full h-14 text-lg font-semibold bg-green-600 hover:bg-green-700"
                    size="lg"
                  >
                    Complete Practice
                  </Button>
                ) : onNextExpression ? (
                  <Button
                    onClick={() => {
                      setFeedback(null);
                      onNextExpression();
                    }}
                    className="w-full h-14 text-lg font-semibold bg-primary hover:bg-primary/90"
                    size="lg"
                  >
                    Next Sentence
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
